<template>
  <div>
    <v-simple-table dense :class="'cartItems'" class="">
      <template v-slot:default>
        <thead>
        <tr>
          <th class="text-center pink lighten-5" width="50%">Product</th>
          <th class="text-center pink lighten-5" width="10%">$ Markup</th>
          <th class="text-center pink lighten-5" width="10%">% Markup</th>
          <th class="text-center pink lighten-5" width="10%">Quantity</th>
          <th class="text-right pink lighten-5" width="10%">Retail Rate</th>
          <th class="text-no-wrap text-left pink lighten-5" width="10%">
            Retail Total
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in getCart().items" :key="item.item.id">
          <td>
            {{ item.item.name }}
          </td>
          <td>
            <number-field
              :options="{ type: `dollar` }"
              v-if="getSelectedTradingRole !== TradingRoles.SALES_REP"
              v-model="item.retailFlatMarkup"
              @input="
                  updateItemRetailData(item, {
                    retailPercentMarkup: null,
                    retailFlatMarkup: $event,
                  })
                "
            />
            <number-field
              v-else
              :options="{ type: `dollar` }"
              v-model="item.retailFlatMarkupRep"
              @input="
                  updateItemRetailData(item, {
                    retailPercentMarkupRep: null,
                    retailFlatMarkupRep: $event,
                  })
                "
            />
          </td>
          <td>
            <number-field
              :options="{ type: `percent` }"
              v-if="getSelectedTradingRole !== TradingRoles.SALES_REP"
              v-model="item.retailPercentMarkup"
              @input="
                  updateItemRetailData(item, {
                    retailPercentMarkup: $event,
                    retailFlatMarkup: null,
                  })
                "
            />
            <number-field
              v-else
              :options="{ type: `percent` }"
              v-model="item.retailPercentMarkupRep"
              @input="
                  updateItemRetailData(item, {
                    retailPercentMarkupRep: $event,
                    retailFlatMarkupRep: null,
                  })
                "
            />
          </td>
          <td class="text-right">{{ item.quantity }}</td>
          <td class="text-right">
            {{
              applyRetailMarkup(computeUnitPrice(item.item), item)
                | toCurrency
            }}
          </td>
          <td class="text-right">
            {{
              (applyRetailMarkup(computeUnitPrice(item.item), item) *
                item.quantity)
                | toCurrency
            }}
          </td>
        </tr>
        <tr>
          <td colspan="5" class="text-right">
            <span class="text-right text-no-wrap">Retail Subtotal</span>
          </td>
          <td class="text-right">
            {{ totals().retailSubtotal | toCurrency }}
          </td>
        </tr>
        <template v-if="buySell === 'buy'">
          <tr
            v-for="(globalMarkup, index) in retailGlobalMarkups"
            :key="index"
          >
            <td colspan="5">
              <v-btn
                class="float-right pa-0"
                @click="removeGlobalMarkup(index)"
                icon
                color="red"
              >
                <v-icon>mdi-minus-box</v-icon>
              </v-btn>
              <span class="float-right text-no-wrap mt-2">{{
                  globalMarkup.label
                }}</span>
            </td>
            <td class="text-right">
              {{ globalMarkup.price | toCurrency }}
            </td>
          </tr>
          <tr>
            <td colspan="3"></td>
            <td colspan="2" class="text-right">
              <v-btn
                class="float-right mt-4"
                @click="addGlobalMarkup()"
                icon
                color="green"
              >
                <v-icon>mdi-plus-box</v-icon>
              </v-btn
              >
              <v-text-field
                type="text"
                label="Label"
                hint="Optional Charge"
                :persistent-hint="true"
                v-model="newGlobalMarkupLabel"
              />
            </td>
            <td>
              <number-field
                :options="{ type: `dollar` }"
                label="Price"
                v-model="newGlobalMarkupPrice"
              />
            </td>
          </tr>
        </template>
        <tr v-if="buySell === `buy` && getBuyHandlingFee > 0">
          <td></td>
          <td>
            <number-field
              :options="{ type: `dollar` }"
              v-model="retailHandlingFlatMarkup"
              @input="retailHandlingPercentMarkup = null"
            />
          </td>
          <td>
            <number-field
              :options="{ type: `percent` }"
              v-model="retailHandlingPercentMarkup"
              @input="retailHandlingFlatMarkup = null"
            />
          </td>
          <td colspan="2">
            <span class="float-right text-no-wrap"> Handling </span>
          </td>
          <td class="text-right">
            {{ totals().retailHandling | toCurrency }}
          </td>
        </tr>
        <tr>
          <td colspan="5">
            <span class="font-weight-bold float-right">Retail Total</span>
          </td>
          <td class="font-weight-bold text-right text-no-wrap">
            {{ totals().retailTotal | toCurrency }}
          </td>
        </tr>
        <tr>
          <td colspan="5">
            <span class="float-right">Gross Profit Total</span>
          </td>
          <td class="text-right">
            {{
              (totals().grossProfitTotal * (buySell === 'buy' ? 1 : -1))
                | toCurrency
            }}
          </td>
        </tr>
        <tr>
          <td colspan="5">
            <span class="float-right">Gross Profit Margin</span>
          </td>
          <td class="text-right">
            {{
              totals().grossProfitPercentage * (buySell === 'buy' ? 1 : -1)
            }}%
          </td>
        </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
import productMixin from '@/mixins/product'
import {mapGetters, mapState} from 'vuex'
import {TradingRoles} from '@/util/constants'
import NumberField from '@/components/NumberField'

export default {
  components: {NumberField},
  mixins: [productMixin],
  props: ['buySell'],
  data() {
    return {
      newGlobalMarkupLabel: null,
      newGlobalMarkupPrice: null,
      TradingRoles,
    }
  },
  computed: {
    ...mapState({
      spotPrices: (state) => state.user.tradeTicketSpotPrices,
    }),
    ...mapGetters([
      'getBuyCart',
      'getSellCart',
      'getBuyHandlingFee',
      'getSelectedTradingRole',
      'getSelectedCompany',
    ]),
    retailTotal: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.retailTotal`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.retailTotal`,
          value,
        })
      },
    },
    retailHandlingPercentMarkup: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.retailHandlingPercentMarkup`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.retailHandlingPercentMarkup`,
          value,
        })
      },
    },
    retailHandlingFlatMarkup: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.retailHandlingFlatMarkup`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.retailHandlingFlatMarkup`,
          value,
        })
      },
    },
    retailGlobalMarkups: {
      get() {
        return this.$store.getters['getCartField'](
          `${this.buySell}.retailGlobalMarkups`
        )
      },
      set(value) {
        this.$store.commit('updateCartField', {
          path: `${this.buySell}.retailGlobalMarkups`,
          value,
        })
      },
    },
  },
  methods: {
    addGlobalMarkup() {
      const globalMarkups = [...this.retailGlobalMarkups]
      globalMarkups.push({
        label: this.newGlobalMarkupLabel,
        price: this.newGlobalMarkupPrice,
      })
      this.retailGlobalMarkups = [...globalMarkups]
      this.newGlobalMarkupLabel = null
      this.newGlobalMarkupPrice = null
    },
    removeGlobalMarkup(indexToRemove) {
      const globalMarkups = [...this.retailGlobalMarkups].filter(
        (item, index) => index !== indexToRemove
      )
      this.retailGlobalMarkups = [...globalMarkups]
    },
    updateItemRetailData(item, event) {
      const {
        retailFlatMarkup,
        retailPercentMarkup,
        retailFlatMarkupRep,
        retailPercentMarkupRep,
      } = Object.assign(item, event)
      const payload = {
        item: item.item,
        buySell: this.buySell,
        retailFlatMarkup,
        retailPercentMarkup,
        retailFlatMarkupRep,
        retailPercentMarkupRep,
      }
      this.$store.dispatch('updateItemRetailData', payload)
    },
    totals() {
      let {retail, wholesale} = this.subtotals()
      let retailHandling =
        this.buySell === `buy`
          ? this.applyRetailMarkup(parseFloat(this.getBuyHandlingFee), {
            retailFlatMarkup: parseFloat(this.retailHandlingFlatMarkup),
            retailPercentMarkup: parseFloat(this.retailHandlingPercentMarkup),
          })
          : 0
      const markupTotal = this.retailGlobalMarkups.reduce(
        (accumulator, item) => {
          accumulator += parseFloat(item.price)
          return accumulator
        },
        0
      )
      const wholesaleTotal =
        wholesale +
        (this.buySell === `buy` ? parseFloat(this.getBuyHandlingFee) : 0)
      const retailTotal = retail + retailHandling + markupTotal
      if (parseFloat(this.retailTotal) !== parseFloat(retailTotal))
        this.retailTotal = retailTotal // update in store since we send this with the order submission
      return {
        retailTotal,
        retailSubtotal: retail,
        retailHandling,
        grossProfitTotal: retailTotal - wholesaleTotal,
        grossProfitPercentage:
          Math.round(((retailTotal - wholesaleTotal) / retailTotal) * 10000) /
          100,
      }
    },
    getCart() {
      return this.buySell === 'buy' ? this.getBuyCart : this.getSellCart
    },
    computeUnitPrice: function (item) {
      return this.buySell === 'buy'
        ? this.computePrices(
          item,
          this.spotPrices,
          this.getSelectedCompany,
          TradingRoles.SALES_MANAGER
        ).ask
        : this.computePrices(
          item,
          this.spotPrices,
          this.getSelectedCompany,
          TradingRoles.SALES_MANAGER
        ).bid
    },
    applyRetailMarkup: function (price, item) {
      let markedUpPrice = price
      if (item.retailFlatMarkup || item.retailPercentMarkup)
        markedUpPrice = item.retailFlatMarkup
          ? Math.round(
          (markedUpPrice + parseFloat(item.retailFlatMarkup)) * 100
        ) / 100
          : Math.round(
          markedUpPrice *
          (1 + parseFloat(item.retailPercentMarkup) / 100) *
          100
        ) / 100
      if (item.retailFlatMarkupRep || item.retailPercentMarkupRep)
        markedUpPrice = item.retailFlatMarkupRep
          ? Math.round(
          (markedUpPrice + parseFloat(item.retailFlatMarkupRep)) * 100
        ) / 100
          : Math.round(
          markedUpPrice *
          (1 + parseFloat(item.retailPercentMarkupRep) / 100) *
          100
        ) / 100
      return markedUpPrice
    },
    subtotals() {
      const cart = this.getCart()
      return cart.items.reduce(
        (accumulator, item) => {
          accumulator.wholesale +=
            this.computeUnitPrice(item.item) * item.quantity
          accumulator.retail +=
            this.applyRetailMarkup(this.computeUnitPrice(item.item), item) *
            item.quantity
          return accumulator
        },
        {retail: 0, wholesale: 0}
      )
    },
  },
}
</script>

<style>
.cartItems table {
  border: 1px solid #eaebed;
}

.cartItems table thead {
  background-color: #f7f8fa;
  color: #888e99;
  text-transform: uppercase;
}

/*.cartItems table td {
  outline: 1px solid #ccc;
}*/
</style>
